import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/solid';
import { useTranslation } from 'react-i18next';

const CompanionRelationship: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [selectedRelationship, setSelectedRelationship] = useState<string>('');

  const relationshipOptions = [
    {
      value: 'romantic',
      label: t('companionCreation.romantic', 'Romantic Partner'),
      emoji: '💕',
      description: t(
        'companionCreation.romanticDescription',
        'A loving, affectionate partner who cares deeply about you',
      ),
    },
    {
      value: 'friend',
      label: t('companionCreation.friend', 'Friend'),
      emoji: '🤝',
      description: t(
        'companionCreation.friendDescription',
        'A supportive, fun friend to hang out with and share laughs',
      ),
    },
  ];

  const handleContinue = () => {
    if (selectedRelationship) {
      sessionStorage.setItem('companionRelationshipType', selectedRelationship);
      navigate('/companion/personality');
    }
  };

  const handleSkip = () => {
    sessionStorage.setItem('companionRelationshipType', 'romantic');
    navigate('/companion/personality');
  };

  return (
    <div className="min-h-screen bg-dark-bg dark:bg-dark-bg bg-light-bg flex flex-col">
      <div className="mobile-container flex-1 flex flex-col">
        {/* Header */}
        <div className="pt-safe px-6 py-4">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-dark-surface dark:hover:bg-dark-surface hover:bg-light-surface"
          >
            <ArrowLeftIcon className="w-5 h-5 text-light-text dark:text-dark-text" />
          </button>
        </div>

        {/* Main content */}
        <div className="flex-1 flex flex-col px-6 pb-8">
          {/* Progress indicator - now 5 steps */}
          <div className="flex space-x-2 mb-8">
            <div className="h-1 flex-1 bg-primary-500 rounded-full" />
            <div className="h-1 flex-1 bg-primary-500 rounded-full" />
            <div className="h-1 flex-1 bg-primary-500 rounded-full" />
            <div className="h-1 flex-1 bg-light-surface dark:bg-dark-surface rounded-full" />
            <div className="h-1 flex-1 bg-light-surface dark:bg-dark-surface rounded-full" />
            <div className="h-1 flex-1 bg-light-surface dark:bg-dark-surface rounded-full" />
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-light-text dark:text-dark-text mb-2">
            {t('companionCreation.relationshipTitle', 'What kind of relationship do you want?')}
          </h1>
          <p className="text-light-text-secondary dark:text-dark-text-secondary mb-8">
            {t(
              'companionCreation.relationshipSubtitle',
              'This determines how your companion interacts with you',
            )}
          </p>

          {/* Relationship options */}
          <div className="space-y-3 mb-auto">
            {relationshipOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => setSelectedRelationship(option.value)}
                className={`w-full p-4 rounded-2xl border-2 transition-all ${
                  selectedRelationship === option.value
                    ? 'border-primary-500 bg-primary-500/10'
                    : 'border-light-border dark:border-dark-border bg-white dark:bg-dark-surface hover:border-gray-400 dark:hover:border-dark-text-secondary'
                }`}
              >
                <div className="flex items-center space-x-4">
                  <span className="text-3xl">{option.emoji}</span>
                  <div className="flex-1 text-left">
                    <div className="font-semibold text-light-text dark:text-dark-text">
                      {option.label}
                    </div>
                    <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                      {option.description}
                    </div>
                  </div>
                  {selectedRelationship === option.value && (
                    <div className="w-6 h-6 rounded-full bg-primary-500 flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>

          {/* Buttons */}
          <div className="space-y-3">
            <button
              onClick={handleContinue}
              disabled={!selectedRelationship}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('common.continue', 'Continue')}
            </button>
            <button onClick={handleSkip} className="btn-secondary">
              {t('companionCreation.skipForNow', 'Skip for now')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanionRelationship;
